import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { ToastContainer, toast } from 'react-toastify'
import axios from 'axios'
import moment from 'moment'

import Navbar from '../../components/Navbar'
import Star from '../../components/Star'
import { addToCart } from '../../redux/reducers/cartSlice'
import ld from '../../images/load.gif'
import noimage from '../../images/noimage.jpg'
import styles from '../../styles/product.module.css'

const notify = (message, type = 'success') =>
  toast[type](message, { position: 'bottom-right', theme: 'colored' })

function Product() {
  const [data, setData] = useState(null)
  const [rating, setRating] = useState('')
  const [comment, setComment] = useState('')

  const { users } = useSelector((state) => state)
  const isLoggedIn = users?.isloggedin

  const { id } = useParams()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const getData = async () => {
    try {
      const res = await axios.get(`https://pro-shop-backend.vercel.app/api/products/${id}`)
      if (res.data.success) {
        setData(res.data.message)
      } else {
        toast.error(res.data.message || 'Failed to fetch product details', {
          position: 'bottom-right',
          theme: 'colored'
        })
        navigate('/')
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch product details'
      toast.error(errorMessage, {
        position: 'bottom-right',
        theme: 'colored'
      })
      navigate('/')
    }
  }

  useEffect(() => {
    getData()
  }, [id])

  const validate = () => {
    if (!isLoggedIn) {
      toast.error('Please login to submit a review', {
        position: 'bottom-right',
        theme: 'colored'
      })
      navigate('/login')
      return false
    }
    if (!rating) {
      notify('Please provide a rating', 'error')
      return false
    }
    if (!comment.trim()) {
      notify('Please provide a comment', 'error')
      return false
    }
    return true
  }

  const commentHandler = async (e) => {
    e.preventDefault()
    if (!validate()) return

    try {
      const res = await axios.post(`https://pro-shop-backend.vercel.app/api/products/${data._id}/reviews`, { rating, comment })
      if (res.data.success === true) {
        toast.success(res.data.message || 'Review added successfully', {
          position: 'bottom-right',
          theme: 'colored'
        })
        setComment('')
        setRating('')
        getData()
      } else {
        toast.error(res.data.message || 'Failed to add review', {
          position: 'bottom-right',
          theme: 'colored'
        })
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to add review. Please try again.'
      toast.error(errorMessage, {
        position: 'bottom-right',
        theme: 'colored'
      })
    }
  }

  const cartHandler = () => {
    if (data.countInStock === 0) {
      notify('Item is out of stock', 'error')
      return
    }
    dispatch(addToCart({ ...data, quantity: 1, product: data._id }))
    notify('Item added to cart successfully')
  }

  if (data === null) {
    return (
      <div>
        <Navbar />
        <div className={styles.loading}>
          <img src={ld} className={styles.loaderImage} alt="loader" />
        </div>
      </div>
    )
  }

  return (
    <div>
      <Navbar />

      <div className={styles.wrapper}>
        <div className={styles.mainSection}>
          <div className={styles.imageColumn}>
            <img src={data.image || noimage} className={styles.image} alt={data.name} />
          </div>

          <div className={styles.infoColumn}>
            <h1 className={styles.name}>{data.name}</h1>
            <Star stars={data.rating} reviews={data.numReviews} />

            <div className={styles.price}>${data.price}</div>

            <div className={styles.stock}>
              {data.countInStock !== 0 ? (
                <span className={styles.inStock}>{data.countInStock} items in stock</span>
              ) : (
                <span className={styles.outStock}>Out of stock</span>
              )}
            </div>

            <button
              type="button"
              disabled={data.countInStock === 0}
              className={styles.addToCart}
              onClick={cartHandler}
            >
              Add to Cart
            </button>

            <div className={styles.descriptionBlock}>
              <h4>Description</h4>
              <p>{data.description}</p>
            </div>
          </div>
        </div>

        <div className={styles.reviewsSection}>
          <h3 className={styles.sectionHeading}>Reviews</h3>

          {isLoggedIn && (
            <form className={styles.reviewForm} onSubmit={commentHandler}>
              <h4 className={styles.reviewFormTitle}>Add a product review</h4>

              <select value={rating} onChange={(e) => setRating(e.target.value)}>
                <option value="">Rate this product</option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
                <option value="5">5</option>
              </select>

              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className={styles.commentInput}
                autoComplete="off"
                placeholder="Write your comment here..."
              />

              <button type="submit" className={styles.commentSubmit}>
                Submit
              </button>
            </form>
          )}

          <div className={styles.reviewsList}>
            {data.reviews.length === 0 ? (
              <p className={styles.noReviews}>No reviews yet</p>
            ) : (
              data.reviews.map((review) => (
                <div className={styles.reviewCard} key={review._id}>
                  <div className={styles.reviewHead}>
                    <h5>{review.name}</h5>
                    <span className={styles.verified}>Verified</span>
                  </div>
                  <div className={styles.reviewDate}>{moment(review.updatedAt).format('ddd Do MMM Y')}</div>
                  <Star stars={review.rating} />
                  <p className={styles.reviewComment}>{review.comment}</p>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <ToastContainer
        position="bottom-right"
        theme="colored"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  )
}

export default Product